import { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useMutation } from '@tanstack/react-query';
import { useDisclosure } from '@chakra-ui/react';
import { toast } from 'react-toastify';
import {
    findObjectsWithSameNumber,
    validateBlockSizeRanges,
} from 'utils/numberBlockUtils';
import { postNumberBlocks } from 'API/NumberBlocks/postNumberBlocks';
import { postNumberBlocksToOc } from 'API/NumberBlocks/postNumberBlocksToOc';
import { useGetCountriesList } from 'pages/telephony/numbers/queries/useGetCountriesList';
import { SET_TPM_COUNTRIES } from '@/store/constants/service';
import { delay } from 'utils/utils';
import getRuntimeAppConfig from 'utils/runtimeAppConfig';
import {
    initTableState,
    PROGRESS_COMPLETE_DELAY_MS,
    getCallingCode,
} from '../constants';

const config = getRuntimeAppConfig();

/**
 * Single-country-per-order rule (2267). Returns the ids of the minority
 * (outlier) rows, treating US + CA (+1) as one group. Ties are broken by the
 * first row's group. Extracted as a pure helper so the debounced effect and
 * the synchronous submit guard share one implementation.
 */
function computeCountryMismatchRows(aggregateTable) {
    // Always derive the calling code from the +E.164 number itself so that
    // pasted rows (which never fire onChange and thus have countryCode='')
    // are detected consistently alongside typed rows.
    const rowsWithCountry = aggregateTable
        .filter((r) => r.number)
        .map((r) => ({
            ...r,
            _callingCode: getCallingCode(r.number),
        }))
        .filter((r) => r._callingCode);

    if (rowsWithCountry.length < 2) return [];

    // US + CA share +1 and are allowed together in the same order.
    const toGroup = (code) => (code === '1' ? '_northamerica' : code);

    const groupCounts = {};
    for (const row of rowsWithCountry) {
        const g = toGroup(row._callingCode);
        groupCounts[g] = (groupCounts[g] || 0) + 1;
    }

    const firstGroup = toGroup(rowsWithCountry[0]._callingCode);
    let dominantGroup = firstGroup;
    let dominantCount = groupCounts[firstGroup];
    for (const [g, count] of Object.entries(groupCounts)) {
        if (count > dominantCount) {
            dominantGroup = g;
            dominantCount = count;
        }
    }

    return rowsWithCountry
        .filter((r) => toGroup(r._callingCode) !== dominantGroup)
        .map((r) => r.id);
}

export function getApiErrorMessage(error, fallback = 'Request failed') {
    const data = error?.response?.data;

    if (typeof data === 'string') {
        return data;
    }

    if (Array.isArray(data)) {
        return data.filter(Boolean).join(', ') || fallback;
    }

    if (data?.message) {
        return data.message;
    }

    if (data?.errors && typeof data.errors === 'object') {
        const errorKeys = Object.keys(data.errors).map((key) =>
            key.toLowerCase(),
        );
        if (
            errorKeys.includes('companyid') ||
            errorKeys.includes('partnerid')
        ) {
            return 'Partner and Company are required to upload these numbers to Operator Connect. Please define these in the order request.';
        }

        const messages = Object.values(data.errors).flat().filter(Boolean);
        if (messages.length > 0) {
            return messages.join(', ');
        }
    }

    return data?.title ?? error?.message ?? fallback;
}

export function useAddNumberBlock() {
    const dispatch = useDispatch();

    const [aggregateTable, setAggregateTable] = useState(initTableState);
    const [hasError, setHasError] = useState(false);
    const [carrierCount, setCarrierCount] = useState(0);
    const [hasEmptyNumber, setHasEmptyNumber] = useState(false);
    const [hasOverlapBlocksize, setHasOverlapBlocksize] = useState(false);
    const [isProcessing, setIsProcessing] = useState(false);
    const [uploadMethod, setUploadMethod] = useState('liveUpload');
    const [countryMismatchRows, setCountryMismatchRows] = useState([]);
    const [chunkProgress, setChunkProgress] = useState({
        current: 0,
        total: 0,
    });
    const [chunkResults, setChunkResults] = useState([]);
    // Create Order (queued) submit state (2265)
    const [isOrderSubmit, setIsOrderSubmit] = useState(false);
    const [orderResult, setOrderResult] = useState(null);

    const { isOpen, onOpen, onClose } = useDisclosure();

    const { mutateAsync: submitBlocks } = useMutation({
        mutationFn: ({ payload, queue = false }) =>
            postNumberBlocks(payload, queue),
    });
    const { mutateAsync: submitBlocksToOc } = useMutation({
        mutationFn: (payload) => postNumberBlocksToOc(payload),
    });

    const { state } = useSelector((s) => ({ ...s.modal }));
    const { currentCompany, currentWholesaler } = useSelector(
        (s) => s.navigationLists,
    );
    const { countries: countryList } = useSelector((s) => s.service.tpm);
    const { requiredScope } = useSelector((s) => ({ ...s.login }));
    const { darkMode } = useSelector((s) => s.settings);

    const createOrderWholesalerList = config.createOrderWholesalerList ?? [];
    const isCreateOrderWholesaler =
        createOrderWholesalerList.includes(currentWholesaler);

    const initialValues = {
        wholesalerID: state?.wholesalerID || '',
        partnerID: state?.partnerID || '',
        companyID: state?.companyID || '',
        companyIsTrial: state?.companyIsTrial ?? null,
        inboundCarrierID: state?.inboundCarrierID || '',
        outboundCarrierID: state?.outboundCarrierID || '',
        publicPool: state?.publicPool || false,
        globalEntity: state?.globalEntity || false,
        numberBlockPortingType: state?.numberBlockPortingType || 0,
        internalNotes: state?.internalNotes || '',
        attribute1: state?.attribute1 || '',
        attribute2: state?.attribute2 || '',
        attribute3: state?.attribute3 || '',
        usage: state?.usage || '',
    };

    const { data: countries } = useGetCountriesList({ currentCompany });

    useEffect(() => {
        const processedCountries = countries?.map((c) =>
            c?.['alpha2']?.toLowerCase(),
        );
        if (
            processedCountries instanceof Array &&
            !Array.isArray(countryList)
        ) {
            dispatch({ type: SET_TPM_COUNTRIES, payload: processedCountries });
        }
    }, [countries, countryList, dispatch]);

    // Debounced: validateBlockSizeRanges is O(n²), so running it on every row
    // change made 1000+ row pastes very expensive. Run it ~300ms after the
    // table settles instead.
    useEffect(() => {
        const handle = setTimeout(() => {
            setHasOverlapBlocksize(validateBlockSizeRanges(aggregateTable));
            if (hasOverlapBlocksize) {
                setHasError(true);
            } else {
                setHasError(false);
            }
        }, 300);
        return () => clearTimeout(handle);
    }, [aggregateTable, hasOverlapBlocksize]);

    useEffect(() => {
        if (!aggregateTable?.length) {
            setAggregateTable(initTableState);
        }
    }, [aggregateTable]);

    // NOTE: fix useRef persist value at first row between render
    useEffect(() => {
        const clearButton = document.querySelector('button#Clear-0');
        if (clearButton) {
            clearButton.click();
        }
    }, []);

    // Wholesalers in createOrderWholesalerList (e.g. Softbank) default to the
    // Create Order method and never see the Upload Method selector (2319).
    // Membership drives the default both ways so switching wholesaler context
    // resets a non-listed wholesaler back to the standard Live Upload default.
    useEffect(() => {
        setUploadMethod(isCreateOrderWholesaler ? 'createOrder' : 'liveUpload');
    }, [isCreateOrderWholesaler]);

    useEffect(() => {
        if (!isCreateOrderWholesaler) {
            return;
        }

        setAggregateTable((prevTable) => {
            if (!prevTable?.some((row) => row.blockSize !== 1)) {
                return prevTable;
            }

            return prevTable.map((row) => ({
                ...row,
                blockSize: 1,
            }));
        });
    }, [isCreateOrderWholesaler]);

    // Validate single-country-per-order rule (2267), debounced so it doesn't
    // re-run on every row change during a paste. See computeCountryMismatchRows.
    useEffect(() => {
        const handle = setTimeout(() => {
            setCountryMismatchRows(computeCountryMismatchRows(aggregateTable));
        }, 300);
        return () => clearTimeout(handle);
    }, [aggregateTable]);

    const handleFieldValueChange = (field, value) => {
        if (field === 'wholesalerID') {
            dispatch({
                type: 'CHANGE_MODAL_STATE',
                payload: {
                    numberBlockPortingType: 0,
                    wholesalerID: value,
                    partnerID: null,
                    companyID: null,
                    outboundCarrierID: value ? null : state?.outboundCarrierID,
                    inboundCarrierID: value ? null : state?.inboundCarrierID,
                    usage: '',
                    noNeedPrompt: true,
                },
            });
        } else if (field === 'partnerID') {
            dispatch({
                type: 'CHANGE_MODAL_STATE',
                payload: {
                    numberBlockPortingType: 0,
                    partnerID: value,
                    companyID: value ? state?.companyID : null,
                    noNeedPrompt: true,
                },
            });
        } else {
            dispatch({
                type: 'CHANGE_MODAL_STATE',
                payload: { [field]: value, noNeedPrompt: true },
            });
        }
    };

    const onError = (payload) => {
        dispatch({ type: 'CHANGE_MODAL_BACKEND_ERROR', payload });
    };

    const handleModalSubmit = async (values) => {
        // Validation is debounced, so a submit fired inside the debounce window
        // could otherwise run against stale gate state. Re-validate the whole
        // table synchronously here and abort if anything fails.
        const overlap = validateBlockSizeRanges(aggregateTable);
        const duplicates = findObjectsWithSameNumber(aggregateTable).filter(
            (r) => Boolean(r?.number) && r.number !== '',
        );
        const hasEmpty = aggregateTable?.some((row) => row.number === '');
        const hasZeroWarning = aggregateTable?.some((row) =>
            (row.number || '').replace(/\D/g, '').startsWith('0'),
        );
        const mismatchRows = computeCountryMismatchRows(aggregateTable);
        const missingUsage = isCreateOrderWholesaler && !values.usage;

        if (
            overlap ||
            duplicates.length > 0 ||
            hasEmpty ||
            hasZeroWarning ||
            mismatchRows.length > 0 ||
            missingUsage
        ) {
            // Sync the gate state so highlights and the disabled button catch up.
            setHasOverlapBlocksize(overlap);
            setHasError(overlap || duplicates.length > 0 || hasZeroWarning);
            setHasEmptyNumber(hasEmpty);
            setCountryMismatchRows(mismatchRows);
            if (missingUsage) {
                toast.error('Please select Usage Type for this order.');
            }
            return false;
        }

        const chunkSize = config.numberBlockChunkSize ?? 10;

        const headerFields = {
            inboundCarrierID: values.inboundCarrierID,
            outboundCarrierID: values.outboundCarrierID,
            publicPool: values.publicPool,
            globalEntity: values.globalEntity,
            wholesalerID: values.wholesalerID,
            partnerID: values.partnerID,
            companyID: values.companyID,
            numberBlockPortingType: values.numberBlockPortingType,
            internalNotes: values.internalNotes,
            attribute1: values.attribute1,
            attribute2: values.attribute2,
            attribute3: values.attribute3,
        };

        const allItems = aggregateTable.map((row) => {
            const raw = row.number;
            const first = raw?.startsWith('+') ? raw.slice(1) : raw || '';
            return {
                blockSize: isCreateOrderWholesaler
                    ? 1
                    : parseInt(row.blockSize),
                first,
                _row: row,
            };
        });

        // Create Order (2263/2264/2265): queue the whole request in a single
        // POST /NumberBlocks?queue=true instead of processing it live in chunks.
        if (uploadMethod === 'createOrder') {
            const items = allItems.map(({ blockSize, first }) => ({
                blockSize,
                first,
            }));

            setIsOrderSubmit(true);
            // total known immediately so the "Requested order" step can render
            setOrderResult({ total: items.length });
            setIsProcessing(true);
            onOpen();

            try {
                const response = isCreateOrderWholesaler
                    ? await submitBlocksToOc({
                          ...headerFields,
                          usage: values.usage,
                          items,
                      })
                    : await submitBlocks({
                          payload: { ...headerFields, items },
                          queue: true,
                      });
                const messages = Array.isArray(response.data)
                    ? response.data
                    : [];
                const failedMessages = messages.filter((message) =>
                    String(message).includes('[Failed]'),
                );
                if (failedMessages.length > 0) {
                    throw new Error(failedMessages.join(', '));
                }
                setOrderResult({
                    success: true,
                    httpStatus: response.status,
                    total: items.length,
                });
                toast.success('Number block order created successfully.');
            } catch (err) {
                const errMsg = getApiErrorMessage(err);
                setOrderResult({
                    success: false,
                    httpStatus: err?.response?.status ?? 0,
                    message: errMsg,
                    total: items.length,
                });
                toast.error(errMsg);
            }

            await delay(PROGRESS_COMPLETE_DELAY_MS);
            setIsProcessing(false);
            return false;
        }

        const chunks = [];
        for (let i = 0; i < allItems.length; i += chunkSize) {
            chunks.push(allItems.slice(i, i + chunkSize));
        }

        const isMultiChunk = chunks.length > 1;

        setIsOrderSubmit(false);
        setChunkResults([]);
        setChunkProgress({ current: 0, total: chunks.length });
        setIsProcessing(true);
        onOpen();

        for (let i = 0; i < chunks.length; i++) {
            const chunk = chunks[i];
            const items = chunk.map(({ blockSize, first }) => ({
                blockSize,
                first,
            }));
            const rangeStart = chunk[0]._row.number;
            const rangeEnd = chunk[chunk.length - 1]._row.number;

            if (isMultiChunk) {
                setChunkResults((prev) => [
                    ...prev,
                    {
                        chunkIndex: i,
                        httpStatus: null,
                        range: { start: rangeStart, end: rangeEnd },
                        total: chunk.length,
                        items: [],
                    },
                ]);
            }

            try {
                const response = await submitBlocks({
                    payload: { ...headerFields, items },
                });
                const messages = Array.isArray(response.data)
                    ? response.data
                    : [];
                const chunkItems = chunk.map((item, idx) => {
                    const message = messages[idx] ?? 'Processed';
                    const isError = message.includes('[Failed]');
                    return {
                        number: item._row.number,
                        blockSize: item._row.blockSize,
                        message,
                        isError,
                    };
                });

                if (isMultiChunk) {
                    setChunkResults((prev) =>
                        prev.map((r) =>
                            r.chunkIndex === i
                                ? {
                                      ...r,
                                      httpStatus: response.status,
                                      items: chunkItems,
                                  }
                                : r,
                        ),
                    );
                } else {
                    setChunkResults([
                        {
                            chunkIndex: 0,
                            httpStatus: response.status,
                            range: { start: rangeStart, end: rangeEnd },
                            total: chunk.length,
                            items: chunkItems,
                        },
                    ]);
                }
            } catch (err) {
                const errMsg = getApiErrorMessage(err);
                const httpStatus = err?.response?.status ?? 0;
                const chunkItems = chunk.map((item) => ({
                    number: item._row.number,
                    blockSize: item._row.blockSize,
                    message: errMsg,
                    isError: true,
                }));

                if (isMultiChunk) {
                    setChunkResults((prev) =>
                        prev.map((r) =>
                            r.chunkIndex === i
                                ? { ...r, httpStatus, items: chunkItems }
                                : r,
                        ),
                    );
                } else {
                    setChunkResults([
                        {
                            chunkIndex: 0,
                            httpStatus,
                            range: { start: rangeStart, end: rangeEnd },
                            total: chunk.length,
                            items: chunkItems,
                        },
                    ]);
                }
            }

            setChunkProgress({ current: i + 1, total: chunks.length });
        }

        await delay(PROGRESS_COMPLETE_DELAY_MS);
        setIsProcessing(false);
        return false;
    };

    const isCarrierRequired =
        carrierCount >= 1 &&
        (!state?.inboundCarrierID || !state?.outboundCarrierID);

    return {
        // Form state
        isCarrierRequired,
        setCarrierCount,
        aggregateTable,
        updateAggregateTable: setAggregateTable,
        hasError,
        setHasError,
        hasEmptyNumber,
        setHasEmptyNumber,
        isProcessing,
        initialValues,
        handleModalSubmit,
        handleFieldValueChange,
        onError,
        // Upload method (2264/2319)
        uploadMethod,
        setUploadMethod,
        isCreateOrderWholesaler,
        selectedUsage: state?.usage || '',
        // Country validation (2267)
        countryMismatchRows,
        // Modal state
        isOpen,
        onClose,
        chunkProgress,
        chunkResults,
        // Create Order (queued) submit state (2265)
        isOrderSubmit,
        orderResult,
        // User/UI context
        darkMode,
        requiredScope,
        dispatch,
    };
}
